import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { useCan } from "@/shared/auth";
import { PageHeader } from "@/shared/components/PageHeader";
import { Button } from "@/shared/ui/button";
import { Select } from "@/shared/ui/select";

import { useListNotifications } from "../api/use-list-notifications";
import { useMarkAllRead } from "../api/use-notification-mutations";
import { NotificationCard } from "../components/NotificationCard";
import { NOTIFICATION_TYPES, type NotifFilter } from "../lib/notif";

type ReadFilter = "all" | "unread" | "read";

interface PageFilterState {
  severity: NotifFilter["severity"] | "all";
  type: string;
  read: ReadFilter;
}

const DEFAULT_FILTER: PageFilterState = {
  severity: "all",
  type: "",
  read: "all",
};

const PAGE_SIZE = 50;

export function NotificationsInboxPage(): JSX.Element {
  const { t } = useTranslation(["notifications", "nav"]);
  const [filter, setFilter] = useState<PageFilterState>(DEFAULT_FILTER);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const updateFilter = (updates: Partial<PageFilterState>) => {
    setFilter((prev) => ({ ...prev, ...updates }));
    // Any filter change re-starts pagination at the first page.
    setVisibleCount(PAGE_SIZE);
  };

  const apiFilter: NotifFilter = { offset: 0, limit: visibleCount };
  if (filter.severity !== "all" && filter.severity !== undefined) {
    apiFilter.severity = filter.severity;
  }
  if (filter.type !== "") apiFilter.type = filter.type;
  if (filter.read === "unread") apiFilter.read = false;
  if (filter.read === "read") apiFilter.read = true;

  const { data, isLoading, isError, isFetching } = useListNotifications(apiFilter);
  const markAllRead = useMarkAllRead();
  const canWrite = useCan("notifications:write");

  const items = data?.items ?? [];
  const hasMore = (data?.total ?? 0) > items.length && visibleCount < 200;

  return (
    <div data-testid="notifications-page">
      <PageHeader
        breadcrumb={
          <>
            <span>{t("nav:group.operations")}</span>
            <span className="text-[var(--premium-border-strong)]">/</span>
            <span className="text-[var(--text-secondary)]">{t("notifications:page.title")}</span>
          </>
        }
        title={t("page.title")}
        subtitle={t("page.subtitle", { count: data?.total ?? 0 })}
        actions={
          canWrite ? (
            <Button
              variant="outline"
              size="sm"
              data-testid="page-markall-read"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
            >
              {t("action.mark_all_read")}
            </Button>
          ) : null
        }
      />

      {/* Filter row */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {/* Severity filter */}
        <Select
          data-testid="filter-severity"
          value={filter.severity}
          onChange={(e) =>
            updateFilter({
              severity: e.target.value as PageFilterState["severity"],
            })
          }
          aria-label={t("filter.severity_label")}
        >
          <option value="all">{t("filter.severity_all")}</option>
          <option value="P1">{t("severity.P1")}</option>
          <option value="P2">{t("severity.P2")}</option>
          <option value="P3">{t("severity.P3")}</option>
        </Select>

        {/* Type filter — fixed, complete list; filtered server-side */}
        <Select
          data-testid="filter-type"
          value={filter.type}
          onChange={(e) => updateFilter({ type: e.target.value })}
          aria-label={t("filter.type_label")}
        >
          <option value="">{t("filter.type_all")}</option>
          {NOTIFICATION_TYPES.map((type) => (
            <option key={type} value={type}>
              {t(`settings.event_${type}`, { defaultValue: type })}
            </option>
          ))}
        </Select>

        {/* Read filter */}
        <Select
          data-testid="filter-read"
          value={filter.read}
          onChange={(e) => updateFilter({ read: e.target.value as ReadFilter })}
          aria-label={t("filter.read_label")}
        >
          <option value="all">{t("filter.read_all")}</option>
          <option value="unread">{t("filter.read_unread")}</option>
          <option value="read">{t("filter.read_read")}</option>
        </Select>
      </div>

      {/* Content */}
      {isLoading && (
        <p
          data-testid="notifications-loading"
          className="py-8 text-center text-sm text-[var(--text-subtle)]"
        >
          {t("state.loading")}
        </p>
      )}

      {isError && !isLoading && (
        <p
          data-testid="notifications-error"
          className="py-8 text-center text-sm text-[var(--intent-danger-fg)]"
        >
          {t("state.error")}
        </p>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <p
          data-testid="notifications-empty"
          className="py-8 text-center text-sm text-[var(--text-subtle)]"
        >
          {t("state.empty")}
        </p>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <div className="rounded-xl border border-[var(--premium-border)] bg-white">
          {items.map((event) => (
            <NotificationCard key={event.id} event={event} variant="expanded" />
          ))}
        </div>
      )}

      {hasMore && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="outline"
            size="sm"
            data-testid="load-more"
            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
            disabled={isFetching}
          >
            {t("action.load_more")}
          </Button>
        </div>
      )}
    </div>
  );
}
